import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getLayout, createLayout, updateLayout, deleteLayout, listVenues } from '../api';
import { useTailwindCdn } from '../useTailwindCdn';
import '../layouts-v2.css';

export function LayoutsV2Edit() {
  useTailwindCdn();
  const navigate = useNavigate();
  const { id } = useParams();

  const [layout, setLayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  const [formData, setFormData] = useState({
    name: '',
    venueId: '',
    type: 'SEAT_PLAN',
    description: '',
    maxSeats: 0,
    isActive: true,
  });

  const [venues, setVenues] = useState([]);

  useEffect(() => {
    if (id && id !== 'create') {
      fetchLayout();
    } else {
      setLoading(false);
    }
    fetchVenues();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLayout = async () => {
    try {
      const data = await getLayout(id);
      setLayout(data);
      setFormData({
        name: data.name || '',
        venueId: data.venueId || '',
        type: data.type || 'SEAT_PLAN',
        description: data.description || '',
        maxSeats: data.metadata?.maxSeats || 0,
        isActive: data.isActive ?? true,
      });
    } catch (err) {
      console.error('Error fetching layout:', err);
      setError('Layout yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchVenues = async () => {
    try {
      setVenues(await listVenues());
    } catch (err) {
      console.error('Error fetching venues:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const payload = {
        name: formData.name,
        venueId: formData.venueId,
        type: formData.type,
        description: formData.description,
        isActive: formData.isActive,
      };

      if (id === 'create') {
        const saved = await createLayout(payload);
        navigate(`/layouts-v2/${saved.id}`);
      } else {
        const saved = await updateLayout(id, payload);
        setLayout(saved);
      }
    } catch (err) {
      console.error('Error saving layout:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Bu layoutu silmek istediğinize emin misiniz? Bu işlem geri alınamaz.')) {
      return;
    }

    try {
      await deleteLayout(id);
      navigate('/layouts-v2');
    } catch (err) {
      console.error('Error deleting layout:', err);
      setError('Layout silinemedi');
    }
  };

  const typeOptions = [
    { value: 'SEAT_PLAN', label: 'Koltuk Planı' },
    { value: 'AREA_LAYOUT', label: 'Alan Düzeni' },
    { value: 'MIXED', label: 'Karışık' },
  ];

  if (loading) {
    return (
      <div className="layouts-v2-root flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#AE256D]" />
      </div>
    );
  }

  return (
    <div className="layouts-v2-root max-w-4xl mx-auto space-y-6 mt-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            type="button"
            onClick={() => navigate('/layouts-v2')}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {id === 'create' ? 'Yeni Layout Oluştur' : `Layout Düzenle: ${layout?.name}`}
            </h1>
            <p className="text-gray-600">
              {id === 'create'
                ? 'Mekanınız için yeni bir koltuk düzeni ayarlayın'
                : 'Layout detaylarını ve yapılandırmasını düzenleyin'}
            </p>
          </div>
        </div>

        {id !== 'create' && layout && (
          <button
            type="button"
            onClick={handleDelete}
            className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors"
          >
            Layoutu Sil
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
          <div className="flex-1">
            <p className="text-red-800">{error}</p>
          </div>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            <button
              type="button"
              onClick={() => setActiveTab('details')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'details'
                  ? 'border-[#AE256D] text-[#AE256D]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Layout Detayları
            </button>
            {id !== 'create' && (
              <>
                <button
                  type="button"
                  onClick={() => setActiveTab('seats')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'seats'
                      ? 'border-[#AE256D] text-[#AE256D]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Koltuk Yönetimi
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('zones')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'zones'
                      ? 'border-[#AE256D] text-[#AE256D]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Zone Yapılandırması
                </button>
              </>
            )}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'details' && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Layout Adı *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="text-black w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#AE256D] focus:border-transparent"
                    placeholder="örn. Ana Konser Layoutu"
                  />
                </div>

                <div>
                  <label htmlFor="venueId" className="block text-sm font-medium text-gray-700 mb-2">
                    Mekan *
                  </label>
                  <select
                    id="venueId"
                    name="venueId"
                    value={formData.venueId}
                    onChange={handleInputChange}
                    required
                    className="text-black w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#AE256D] focus:border-transparent"
                  >
                    <option value="">Mekan seçin</option>
                    {venues.map((venue) => (
                      <option key={venue.id} value={venue.id}>
                        {venue.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-2">
                    Layout Tipi
                  </label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="text-black w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#AE256D] focus:border-transparent"
                  >
                    {typeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="maxSeats"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Maksimum Koltuk
                  </label>
                  <input
                    type="number"
                    id="maxSeats"
                    name="maxSeats"
                    value={formData.maxSeats}
                    onChange={handleInputChange}
                    min="0"
                    className="text-black w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#AE256D] focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Açıklama
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  className="text-black w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#AE256D] focus:border-transparent"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-[#AE256D] focus:ring-[#AE256D] border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                  Layout aktif ve kullanıma açık
                </label>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => navigate('/layouts-v2')}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-[#AE256D] text-white rounded-lg hover:bg-[#8B1E55] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving
                    ? 'Kaydediliyor...'
                    : id === 'create'
                      ? 'Layout Oluştur'
                      : 'Değişiklikleri Kaydet'}
                </button>
              </div>
            </form>
          )}

          {activeTab === 'seats' && id !== 'create' && (
            <div className="text-center py-12 text-gray-600">
              Koltuk yönetimi bu ekranda henüz bağlanmadı. Oluşturma sihirbazını kullanın.
            </div>
          )}

          {activeTab === 'zones' && id !== 'create' && (
            <div className="text-center py-12 text-gray-600">
              Zone yapılandırması bu ekranda henüz bağlanmadı. Oluşturma sihirbazını kullanın.
            </div>
          )}
        </div>
      </div>

      {id !== 'create' && layout && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-sm font-medium text-gray-500">Toplam Koltuk</div>
            <div className="text-2xl font-bold text-gray-900">
              {layout._count?.seats || layout.seats?.length || 0}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-sm font-medium text-gray-500">Zone</div>
            <div className="text-2xl font-bold text-gray-900">
              {layout._count?.zones || layout.zones?.length || 0}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-sm font-medium text-gray-500">Etkinlik Kullanımı</div>
            <div className="text-2xl font-bold text-gray-900">
              {layout._count?.eventLayouts || 0}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LayoutsV2Edit;
